import { ErrorBoundaryContext } from '#app/contexts/ErrorBoundaryContext/ErrorBoundaryContext.js';
import { logger } from '#app/services/logger.js';
import {
  captureException,
  type Extras,
  showReportDialog,
  withScope,
} from '@contra/sentry/react';
import { serializeError } from '@contra/utilities/serializeError';
import { Component, type ContextType, type ReactNode } from 'react';

const log = logger.child({
  namespace: 'react:error-boundary',
});

export type ErrorBoundaryRenderArguments = {
  readonly error?: Error;
  report: () => void;
  reset: () => void;
  readonly sentryEventId?: string;
};

type ErrorBoundaryProps = {
  /**
   * A human-readbable identifier for this boundary - this is used to identify the boundary in Sentry.
   */
  readonly boundaryIdentifier: string;
  readonly children: ReactNode;
  readonly extras?: Extras;
  readonly renderError: ({
    error,
    report,
    reset,
    sentryEventId,
  }: ErrorBoundaryRenderArguments) => ReactNode;
  readonly renderRedirectToPage?: ({ route }: { route: string }) => ReactNode;
};

type ErrorBoundaryState = {
  readonly error?: Error;
  readonly hasError: boolean;
  readonly sentryEventId?: string;
};

type ErrorInfo = {
  [key: string]: unknown;
  componentStack: string;
};

const reportDialogFallback = () => {
  log.warn('Report Dialog button clicked, but Sentry not enabled.');
};

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  public static contextType = ErrorBoundaryContext;

  public declare context: ContextType<typeof ErrorBoundaryContext>;

  public constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
    };
  }

  /**
   * @public
   */
  public static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  public componentDidCatch(error: Error | null, errorInfo: ErrorInfo) {
    const { sentryEnabled } = this.context;
    const { extras } = this.props;
    const serializedError = serializeError(error);

    if (sentryEnabled) {
      withScope((scope) => {
        scope.setContext('error properties', serializedError);
        scope.setContext('error info', errorInfo);

        if (extras && Object.keys(extras).length > 0) {
          scope.setExtras(extras);
        }

        scope.setTag('boundary_key', this.props.boundaryIdentifier);

        if (window.posthogSessionId) {
          scope.setContext('posthog', {
            sessionUrl:
              'https://app.posthog.com/recordings?#sessionRecordingId=' +
              window.posthogSessionId,
          });
        }

        const sentryEventId = captureException(error);

        // eslint-disable-next-line react/no-set-state
        this.setState(() => ({
          error: error ?? undefined,
          sentryEventId,
        }));
      });
    } else {
      // We are not using the logger here so we can get some nicer debug info.
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }

  public handleReportDialog = (): void => {
    const { sentryEnabled } = this.context;

    if (sentryEnabled) {
      const { error, sentryEventId } = this.state;

      if (sentryEventId) {
        showReportDialog({
          error,
          eventId: sentryEventId,
          labelSubmit: 'Submit report',
          subtitle:
            'Let us know what you were doing at the time the error occurred.',
          subtitle2: '',
          title: 'Report additional details',
        });
      }
    } else {
      reportDialogFallback();
    }
  };

  public handleReset = (): void => {
    // eslint-disable-next-line react/no-set-state
    this.setState({
      error: undefined,
      hasError: false,
      sentryEventId: undefined,
    });
  };

  public render() {
    const { children, renderError, renderRedirectToPage } = this.props;
    const { error, hasError, sentryEventId } = this.state;

    if (hasError) {
      if (
        error &&
        error.message === 'No paid project was found' &&
        renderRedirectToPage
      ) {
        // Gracefully handle the case where a user is trying to access a project
        // that doesn't exist or user doesn't have access to.
        this.handleReset();
        renderRedirectToPage({ route: '/projects' });
        return null;
      } else {
        return renderError({
          error,
          report: this.handleReportDialog,
          reset: this.handleReset,
          sentryEventId,
        });
      }
    }

    return children;
  }
}
